import { useState } from "react";
import { Alert, Badge, Button, Table, type TableColumn } from "../../ds";
import type { DeviceDetail, NetworkInterface, NetworkStatus, WifiStatus } from "../../lib/api";
import { fmtUptime } from "../../lib/format";
import { useConfirmProbe } from "../../lib/hooks/useConfirmProbe";
import { isUplink } from "../../lib/roles";
import { SectionCard } from "../SectionCard";
import { useSection } from "../../lib/hooks/useSection";
import { ProbeBanner } from "./ProbeBanner";
import { emptyUplinkForm, toUplinkParams, UplinkModal, type UplinkForm } from "./UplinkModal";

// Die Leitungen nach draußen, nebeneinander. Vorher beantwortete das keine
// Sektion: der Modem-Tab zeigte nur LTE, und dass eine Box in Wahrheit über
// einen WLAN-Uplink online ist, stand nirgends.
//
// Hier hängt auch „Uplink hinzufügen“: eine neue Leitung anzulegen gehört dort
// hin, wo die vorhandenen stehen — nicht in drei andere Sektionen verteilt.

/** Muss zu `UPLINK_REVERT_IN` in server/core/writes.py passen. */
const REVERT_IN = 180;

/** Welches WLAN einen Uplink trägt — falls die WLAN-Sektion schon im Cache
 *  liegt. Bewusst ohne eigenen Abruf: die Antwort ist ein Zusatz, kein Grund,
 *  den Router zu wecken. */
function wifiCarrier(wifi: WifiStatus | null, ifaceId: string): string | null {
  for (const radio of wifi?.radios ?? []) {
    for (const net of radio.networks ?? []) {
      if (net.mode === "sta" && net.network === ifaceId) return net.ssid ?? radio.id;
    }
  }
  return null;
}

function columns(wifi: WifiStatus | null): TableColumn<NetworkInterface>[] {
  return [
    {
      key: "up",
      header: "Status",
      cell: (i) => (
        <Badge variant={i.up ? "success" : "danger"} dot>
          {i.up ? "up" : "down"}
        </Badge>
      ),
    },
    {
      key: "id",
      header: "Uplink",
      cell: (i) => {
        const carrier = wifiCarrier(wifi, i.id);
        return (
          <div className="flex flex-col">
            <span className="font-mono text-xs">{i.id}</span>
            {carrier && <span className="text-xs text-content-3">über WLAN: {carrier}</span>}
          </div>
        );
      },
    },
    { key: "proto", header: "Protokoll", cell: (i) => i.proto ?? "—" },
    {
      key: "device",
      header: "Gerät",
      mono: true,
      hideOnMobile: true,
      cell: (i) => i.device ?? "—",
    },
    {
      key: "address",
      header: "Adresse",
      mono: true,
      cell: (i) => i.addresses?.[0] ?? "—",
    },
    {
      key: "metric",
      header: "Metrik",
      hideOnMobile: true,
      cell: (i) => (i.metric !== undefined ? String(i.metric) : "—"),
    },
    {
      key: "uptime",
      header: "Verbunden seit",
      hideOnMobile: true,
      cell: (i) => (i.up ? fmtUptime(i.uptimeSecs) : "—"),
    },
  ];
}

export function UplinksCard({ device }: { device: DeviceDetail }) {
  const section = useSection<NetworkStatus>(device, "network");
  const probe = useConfirmProbe(device.id, section, {
    setAction: "uplink_add",
    confirmAction: "uplink_confirm",
    revertIn: REVERT_IN,
  });
  const [form, setForm] = useState<UplinkForm | null>(null);

  const wifi = (device.sections.wifi?.data as WifiStatus | undefined) ?? null;

  async function submit() {
    if (!form) return;
    const created = await probe.apply(toUplinkParams(form));
    if (created) {
      setForm(null);
      await section.refresh();
    }
  }

  return (
    <>
      <SectionCard
        title="Uplinks"
        description="Alle Leitungen nach draußen — welche gerade trägt, steht in der Metrik"
        section={section}
        deviceOnline={device.online}
        bodyPadding={false}
        actions={
          <Button
            size="sm"
            variant="outline"
            iconName="plus"
            disabled={!device.online || probe.phase === "probe"}
            onClick={() => setForm(emptyUplinkForm())}
          >
            Uplink hinzufügen
          </Button>
        }
        above={
          <>
            {probe.note && (
              <Alert variant={probe.note.variant} dismissible onDismiss={() => probe.setNote(null)}>
                {probe.note.text}
              </Alert>
            )}
            <ProbeBanner
              phase={probe.phase}
              secondsLeft={probe.secondsLeft}
              keeping={probe.keeping}
              hint="Uplink testweise aktiv — Adresse und Erreichbarkeit prüfen."
              onKeep={() => void probe.keep()}
              onDismiss={probe.dismiss}
            />
          </>
        }
      >
        {(data) => (
          <Table
            columns={columns(wifi)}
            rows={(data.interfaces ?? []).filter(isUplink)}
            rowKey={(i) => i.id}
            emptyMessage="Kein Interface mit einem Uplink-Protokoll. Ein statisch konfiguriertes WAN steht unter „LAN“."
          />
        )}
      </SectionCard>

      <UplinkModal
        form={form}
        net={section.data}
        wifi={wifi}
        busy={probe.applying}
        onChange={setForm}
        onClose={() => setForm(null)}
        onSubmit={() => void submit()}
      />
    </>
  );
}
